// KCOS Phase 4 — Living Timeline page.
import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { getTimeline, type TimelineEvent } from "@/lib/timeline.functions";
import { Card } from "@/components/ui/card";

const KIND_LABELS: Record<TimelineEvent["kind"], string> = {
  account_created: "Beginning",
  gate_completed: "Gate",
  activation_completed: "Activation",
  journal_entry: "Journal",
  reading_progress: "Reading",
  challenge_started: "Challenge",
  challenge_completed: "Challenge",
  certificate_issued: "Certificate",
  badge_earned: "Badge",
};

const ALL_FILTERS: TimelineEvent["kind"][] = [
  "gate_completed",
  "activation_completed",
  "journal_entry",
  "reading_progress",
  "challenge_completed",
  "certificate_issued",
  "badge_earned",
];

export const Route = createFileRoute("/_authenticated/journey/timeline")({
  head: () => ({
    meta: [
      { title: "Timeline · Journey · KAfrequency" },
      { name: "description", content: "A living ribbon of every gate opened, activation practiced, and reflection sealed." },
    ],
  }),
  component: TimelinePage,
});

function TimelinePage() {
  const load = useServerFn(getTimeline);
  const [events, setEvents] = useState<TimelineEvent[] | null>(null);
  const [active, setActive] = useState<Set<TimelineEvent["kind"]>>(new Set(ALL_FILTERS));

  useEffect(() => {
    load({ data: { limit: 200 } }).then(setEvents).catch(() => setEvents([]));
  }, [load]);

  const visible = useMemo(
    () => (events ?? []).filter((e) => active.has(e.kind) || e.kind === "account_created"),
    [events, active],
  );

  const toggle = (k: TimelineEvent["kind"]) =>
    setActive((prev) => {
      const next = new Set(prev);
      if (next.has(k)) next.delete(k);
      else next.add(k);
      return next;
    });

  return (
    <div>
      <div className="flex items-baseline justify-between">
        <h1 className="font-serif text-2xl text-gold-luminous">Your Timeline</h1>
        <span className="text-xs text-foreground/50">{events?.length ?? 0} events</span>
      </div>

      <div className="mt-4 flex flex-wrap gap-2">
        {ALL_FILTERS.map((k) => {
          const on = active.has(k);
          return (
            <button
              key={k}
              onClick={() => toggle(k)}
              className={`rounded-full border hairline px-3 py-1 text-xs transition ${
                on ? "bg-gold/20 text-gold-luminous" : "text-foreground/50 hover:text-foreground/80"
              }`}
            >
              {KIND_LABELS[k]}
            </button>
          );
        })}
      </div>

      {events === null ? (
        <div className="mt-8 text-sm text-foreground/50">Loading your ribbon…</div>
      ) : visible.length === 0 ? (
        <Card className="mt-8 p-8 text-center text-sm text-foreground/60">
          Nothing to show yet. <Link to="/activations" className="text-gold underline">Practice something</Link> and it will appear here.
        </Card>
      ) : (
        <ol className="mt-8 relative border-l hairline pl-6">
          {visible.map((e) => (
            <li key={e.id} className="relative pb-6">
              <span className="absolute -left-[27px] top-1.5 h-2 w-2 rounded-full bg-gold" />
              <div className="text-[10px] uppercase tracking-widest text-foreground/50">
                {KIND_LABELS[e.kind]} · {new Date(e.at).toLocaleDateString()}
              </div>
              <div className="mt-1 font-serif text-base text-gold-luminous">
                {e.route ? (
                  <Link to={e.route} className="hover:underline">{e.title}</Link>
                ) : (
                  e.title
                )}
              </div>
              {e.detail && <div className="text-xs text-foreground/60">{e.detail}</div>}
            </li>
          ))}
        </ol>
      )}
    </div>
  );
}
